import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { ProjectService, PurchaseService } from "../../services";
import { contractValidationRules } from "../../pages/subcontractors/validation";
import { get_date } from "../generalComponets";
import { useHasPermission } from "../../utils";
import { ENABLE_CONTRACT_BACKDATE_ENTRY } from "../../constants";
import { markTableReset } from "../../utils/tableStateStorage";

export const AddEditContractForm = ({ subcontractorId, contractId }) => {
  const [actionType] = useState(contractId ? "EDIT" : "ADD");
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [contractDetails, setContractDetails] = useState({
    title: undefined,
    startDate: undefined,
    endDate: undefined,
    contractValue: undefined,
    status: "ACTIVE",
    subcontractorId: Number(subcontractorId),
    projectId: undefined,
    paymentMilestones: [],
    companyId: undefined,
  });

  const enableBackDate = useHasPermission(ENABLE_CONTRACT_BACKDATE_ENTRY);
  const navigate = useNavigate();

  useEffect(() => {
    if (actionType === "ADD") {
      fetchData(subcontractorId);
    } else if (actionType === "EDIT") {
      fetchContractDetails(contractId);
    }
  }, [subcontractorId, contractId, actionType]);

  const fetchData = async (id) => {
    try {
      const { data } = await PurchaseService.getVendorById(id);
      setSelectedSubcontractor(data);
    } catch (error) {
      toast.error("Failed to fetch subcontractor details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const fetchContractDetails = async (id) => {
    try {
      const { data } = await PurchaseService.getContractById(id);
      setContractDetails({
        subcontractorId: data?.subcontractorId,
        title: data?.title,
        description: data?.description,
        contractValue: Number(data?.contractValue),
        startDate: get_date(data?.startDate, "YYYY-MM-DD"),
        endDate: get_date(data?.endDate, "YYYY-MM-DD"),
        projectId: data?.projectId,
      });

      const { subcontractor, project } = data;
      setSelectedSubcontractor(subcontractor);
      setSelectedProject(project);
    } catch (error) {
      toast.error("Failed to fetch contract details");
      console.error("🚀 ~ fetchContractDetails ~ error:", error);
    }
  };

  const handleClearForm = async () => {
    setContractDetails({
      title: undefined,
      description: undefined,
      startDate: undefined,
      endDate: undefined,
      contractValue: undefined,
      status: "ACTIVE",
      subcontractorId: Number(subcontractorId),
      projectId: undefined,
      paymentMilestones: [],
      companyId: undefined,
    });
  };

  const handleSubcontractorChange = (selected) => {
    setSelectedSubcontractor(selected);
    setContractDetails((prevState) => ({
      ...prevState,
      subcontractorId: selected?.id,
    }));
  };

  const handleProjectChange = (selected) => {
    setSelectedProject(selected);
    setContractDetails((prevState) => ({
      ...prevState,
      projectId: selected?.id,
    }));
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setContractDetails((prevState) => ({
      ...prevState,
      [name]: name === "contractValue" ? Number(value) : value,
    }));
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const handleSaveContract = async () => {
    setErrors({});
    const errors = contractValidationRules(contractDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    try {
      setLoading(true);
      if (actionType === "EDIT") {
        const contractDetailsPayload = { ...contractDetails, subcontractorId: undefined };
        await PurchaseService.updateContract(contractId, contractDetailsPayload);
        toast.success("Contract updated successfully");
      } else {
        await PurchaseService.createContract(contractDetails);
        toast.success("Contract added successfully");
        // The list opens fresh, so its filters don't hide the new contract.
        markTableReset();
      }

      handleCancelClick();
    } catch (error) {
      let errorMessage = `Error ${actionType === "EDIT" ? "updating" : "adding"} contract`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.log("🚀 ~ handleSaveContract ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardLayout>
      <Box display="flex" gap={2}>
        <SelectField
          label="Subcontractor"
          value={selectedSubcontractor}
          getData={PurchaseService.getVendors}
          getDataStaticFilters={{ type: "SUBCONTRACTOR" }}
          disabled={!!contractId || !!subcontractorId}
          required
          error={!!errors.subcontractorId}
          helperText={errors.subcontractorId}
          onChange={handleSubcontractorChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Project"
          value={selectedProject}
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["INPROGRESS"] }}
          required
          error={!!errors.projectId}
          helperText={errors.projectId}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />
        <InputField
          required
          label="Title"
          name="title"
          value={contractDetails.title}
          onChange={updateDetailsState}
          error={!!errors.title}
          helperText={errors.title}
          fullWidth
        />
      </Box>

      <Box display="flex" gap={2}>
        <InputField
          required
          label="Contract Amount"
          name="contractValue"
          type="number"
          value={contractDetails.contractValue}
          onChange={updateDetailsState}
          error={!!errors.contractValue}
          helperText={errors.contractValue}
          fullWidth
        />
        <InputField
          required
          label="Start Date"
          name="startDate"
          type="date"
          value={contractDetails.startDate}
          onChange={updateDetailsState}
          error={!!errors.startDate}
          helperText={errors.startDate}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
        <InputField
          required
          label="End Date"
          name="endDate"
          type="date"
          value={contractDetails.endDate}
          onChange={updateDetailsState}
          error={!!errors.endDate}
          helperText={errors.endDate}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <ActionButton buttonText="Cancel" variant="outlined" onButtonClick={handleCancelClick} />
        <ActionButton buttonText="Submit" onButtonClick={handleSaveContract} disabled={loading} />
      </Box>
    </CardLayout>
  );
};
